import React from "react";
import { Search, RefreshCw, Filter, Bookmark, MoreVertical, Grid3X3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useScrollDirection } from "@/hooks/ainews-hooks/useScrollDirection";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import SidepanelIcon from "../../packages/svgs/SidepanelIcon";

interface HeaderProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchSubmit?: () => void;
  onFilterClick?: () => void;
  onRefresh?: () => void;
  onMenuClick?: () => void;
  isRefreshing?: boolean;
  canGenerate?: boolean;
  timeUntilNext?: string;
}

export const Header: React.FC<HeaderProps> = ({
  searchQuery,
  onSearchChange,
  onFilterClick,
  onMenuClick,
  onRefresh,
  isRefreshing,
  canGenerate,
  timeUntilNext,
  onSearchSubmit,
}) => {
  const navigate = useNavigate();
  const { scrollDirection, isAtTop } = useScrollDirection();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDarkMode = useIsDarkMode();

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && onSearchSubmit) {
      onSearchSubmit();
    }
  };

  const isVisible = isAtTop || scrollDirection === "up";

  return (
    <header
      className={`fixed top-0 z-50 w-full glass-header transition-transform duration-300 ${isVisible ? "translate-y-0" : "-translate-y-full"
        }`}
    >
      <div className="container mx-auto xs:px-4 p-2 xs:pt-4">
        <div className="flex items-center justify-between gap-2 xs:gap-3">
          {/* Sidebar toggle button */}
          <button
            onClick={() => onMenuClick()}
            aria-label="Toggle sidebar"
            className={cn(
              "p-1 transition-colors duration-150 rounded-xl flex items-center justify-center",
              isDarkMode
                ? "text-slate-300 hover:text-white hover:bg-white/10"
                : "text-black/90 hover:text-slate-900 hover:bg-slate-200/70"
            )}
          >
            <SidepanelIcon
              className="h-6 w-6"
              isDarkMode={isDarkMode}
            />
          </button>

          <div className="flex-1 max-w-2xl">
            <div className="relative rounded-full overflow-hidden transition-all shadow-sm">
              {/** Simple glassmorphism background: subtle translucent bg + blur + border */}
              <div
                className={cn(
                  "absolute inset-0 z-0 backdrop-blur-md overflow-hidden"
                )}
              />
              <Input
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Search articles..."
                className={cn(
                  "pl-6 h-11 rounded-full bg-transparent backdrop-blur-md border transition-smooth focus-visible:ring-0 focus-visible:ring-offset-0 outline-none relative z-30",
                  searchQuery && searchQuery.trim() ? "pr-12" : "pr-4",
                  isDarkMode
                    ? "text-slate-100 placeholder:text-slate-400 border-white/10 focus:border-white/30"
                    : "text-slate-900 placeholder:text-slate-600 border-slate-200 focus:border-primary/40"
                )}
              />
              {searchQuery && searchQuery.trim() && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSearchSubmit}
                  className={cn(
                    "absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 backdrop-blur-sm transform rounded-full p-0 bg-transparent z-30",
                    isDarkMode
                      ? "text-white"
                      : "text-slate-600 hover:text-primary"
                  )}
                  aria-label="Search"
                >
                  <Search className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-row">
            <div className="hidden sm:flex sm:flex-row space-x-2">
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onFilterClick}
                  className={cn(
                    "h-10 w-10 p-0 rounded-xl border transition-colors bg-transparent backdrop-blur-sm",
                    isDarkMode
                      ? "border-white/5 text-slate-200"
                      : "border-slate-200 text-slate-600"
                  )}
                >
                  <Filter className="w-4 h-4" />
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Filter
                </div>
              </div>
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/bookmark')}
                  className={cn(
                    "h-10 w-10 p-0 rounded-xl border transition-colors bg-transparent backdrop-blur-sm",
                    isDarkMode
                      ? "border-white/5 text-slate-200"
                      : "border-slate-200 text-slate-600"
                  )}
                >
                  <Bookmark className="w-4 h-4" />
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Bookmark
                </div>
              </div>
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/sudoku')}
                  className={cn(
                    "h-10 w-10 p-0 rounded-xl border transition-colors bg-transparent backdrop-blur-sm",
                    isDarkMode
                      ? "border-white/5 text-slate-200"
                      : "border-slate-200 text-slate-600"
                  )}
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Sudoku
                </div>
              </div>
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onRefresh}
                  disabled={isRefreshing || !canGenerate}
                  className={cn(
                    "h-10 w-10 p-0 rounded-xl border transition-colors bg-transparent backdrop-blur-sm group",
                    isDarkMode
                      ? "border-white/5 text-slate-200"
                      : "border-slate-200 text-slate-600"
                  )}
                  aria-label="Refresh news"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </Button>
                <div className="absolute -left-4 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  {!canGenerate && timeUntilNext ? `Wait ${timeUntilNext} to refresh` : 'Refresh'}
                </div>
              </div>
            </div>

            <div className="block sm:hidden relative" ref={dropdownRef}>
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-10 w-10 p-0 rounded-xl border transition-colors",
                    isDarkMode
                      ? "border-white/5 text-slate-200 hover:bg-white/10"
                      : "border-slate-200 text-slate-600 hover:bg-slate-200/70"
                  )}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  More
                </div>
              </div>

              {/* Custom Dropdown */}
              <div className={`
                absolute right-0 top-[120%] w-48 py-2
                ${isDarkMode ? 'bg-[#111017]/90 border-white/10 text-slate-100' : 'bg-white border-slate-200 text-slate-900 shadow-xl'} backdrop-blur-sm rounded-lg shadow-lg
                transform transition-all duration-200 origin-top-right
                ${isDropdownOpen
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}
              `}>
                <button
                  onClick={() => {
                    onFilterClick();
                    setIsDropdownOpen(false);
                  }}
                  className={cn(
                    "w-full px-4 py-2 flex items-center text-sm transition-colors",
                    isDarkMode ? "text-slate-100 hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"
                  )}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </button>
                <button
                  onClick={() => {
                    navigate('/bookmark');
                    setIsDropdownOpen(false);
                  }}
                  className={cn(
                    "w-full px-4 py-2 flex items-center text-sm transition-colors",
                    isDarkMode ? "text-slate-100 hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"
                  )}
                >
                  <Bookmark className="w-4 h-4 mr-2" />
                  Bookmarked Articles
                </button>
                <button
                  onClick={() => {
                    navigate('/sudoku');
                    setIsDropdownOpen(false);
                  }}
                  className={cn(
                    "w-full px-4 py-2 flex items-center text-sm transition-colors",
                    isDarkMode ? "text-slate-100 hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"
                  )}
                >
                  <Grid3X3 className="w-4 h-4 mr-2" />
                  Sudoku
                </button>
                <button
                  onClick={() => {
                    onRefresh();
                    setIsDropdownOpen(false);
                  }}
                  disabled={isRefreshing || !canGenerate}
                  className={cn(
                    "w-full px-4 py-2 flex items-center text-sm transition-colors",
                    isDarkMode
                      ? "text-slate-100 hover:bg-white/10"
                      : "text-slate-700 hover:bg-slate-100",
                    (isRefreshing || !canGenerate) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/** Removed liquid glass SVG filter for a simpler blur/glass effect */}
    </header>
  );
};
